import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect, useState, useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { RelatedPosts } from "@/components/RelatedPosts";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, User, ArrowLeft, Share2, Loader2, Clock } from "lucide-react";
import { XLogo, LinkedinLogo, FacebookLogo, WhatsappLogo } from "@phosphor-icons/react";
import { supabase } from "@/integrations/supabase/client";
import { trackBlogView } from "@/hooks/usePageTracking";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePageSEO } from "@/hooks/usePageSEO";

interface BlogPostData {
  id: string;
  title: string;
  slug: string;
  cover_image: string | null;
  excerpt: string | null;
  author: string;
  published_at: string;
  content: string;
  tags: string[] | null;
  seo_title: string | null;
  seo_description: string | null;
  og_title: string | null;
  og_description: string | null;
  category_id: string | null;
  arabic_title: string | null;
  arabic_excerpt: string | null;
  arabic_content: string | null;
  blog_categories: { name: string } | null;
}

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostData | null>(null);
  const [relatedPosts, setRelatedPosts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { lang, localePath, t } = useLanguage();

  useEffect(() => {
    if (slug) fetchPost(slug);
  }, [slug]);

  const fetchPost = async (slug: string) => {
    setLoading(true);
    const { data, error } = await supabase
      .from("blog_posts")
      .select("*, blog_categories(name)")
      .eq("slug", slug)
      .eq("status", "published")
      .single();

    if (error || !data) {
      setPost(null);
      setLoading(false);
      return;
    }

    setPost(data as BlogPostData);
    trackBlogView(slug, data.id);

    if (data.category_id) {
      const { data: related } = await supabase
        .from("blog_posts")
        .select("id, title, slug, cover_image, excerpt, author, published_at, arabic_title, arabic_excerpt, blog_categories(name)")
        .eq("status", "published")
        .eq("category_id", data.category_id)
        .neq("slug", slug)
        .limit(3);

      if (related) {
        setRelatedPosts(related.map((r: any) => ({
          ...r,
          category: r.blog_categories?.name || "Uncategorized",
          cover_image: r.cover_image || "/placeholder.svg",
          excerpt: r.excerpt || "",
          arabic_title: r.arabic_title,
          arabic_excerpt: r.arabic_excerpt,
        })));
      }
    }
    setLoading(false);
  };

  const seoData = useMemo(() => {
    if (!post) return null;
    const isAr = lang === "ar";
    const pageTitle = isAr && post.arabic_title ? post.arabic_title : (post.seo_title || post.title);
    const pageDesc = isAr && post.arabic_excerpt ? post.arabic_excerpt : (post.seo_description || post.excerpt || "");
    const ogTitle = post.og_title || pageTitle;
    const ogDesc = post.og_description || pageDesc;
    const pageImage = post.cover_image || "";
    const base = "https://www.wavai.ae";
    const postUrl = `${base}/blog/${post.slug}`;
    const displayContent = isAr && post.arabic_content ? post.arabic_content : post.content;
    const wordCount = displayContent.split(/\s+/).length;
    const readingTimeMinutes = Math.max(1, Math.ceil(wordCount / 200));
    const categoryName = post.blog_categories?.name || "Uncategorized";

    return {
      title: pageTitle,
      description: pageDesc,
      ogTitle,
      ogDescription: ogDesc,
      ogImage: pageImage,
      ogType: "article" as const,
      canonical: postUrl,
      hreflang: [
        { lang: "en", href: `${base}/blog/${post.slug}` },
        { lang: "ar", href: `${base}/ar/blog/${post.slug}` },
        { lang: "x-default", href: `${base}/blog/${post.slug}` },
      ],
      articleMeta: {
        publishedTime: post.published_at,
        author: post.author,
        section: categoryName,
        tags: post.tags || undefined,
      },
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BlogPosting",
            "@id": postUrl,
            headline: isAr && post.arabic_title ? post.arabic_title : post.title,
            description: pageDesc,
            image: pageImage || undefined,
            author: { "@type": "Person", name: post.author },
            datePublished: post.published_at,
            dateModified: post.published_at,
            url: postUrl,
            inLanguage: isAr ? "ar" : "en",
            mainEntityOfPage: { "@type": "WebPage", "@id": postUrl },
            wordCount,
            timeRequired: `PT${readingTimeMinutes}M`,
            publisher: {
              "@type": "Organization",
              name: "Wavai",
              url: "https://www.wavai.ae",
              logo: { "@type": "ImageObject", url: "https://www.wavai.ae/logo-wavai.svg" },
            },
            articleSection: categoryName,
            ...(post.tags?.length ? { keywords: post.tags.join(", ") } : {}),
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: base },
              { "@type": "ListItem", position: 2, name: "Blog", item: `${base}/blog` },
              { "@type": "ListItem", position: 3, name: isAr && post.arabic_title ? post.arabic_title : post.title, item: postUrl },
            ],
          },
        ],
      },
    };
  }, [post, lang]);

  usePageSEO(seoData || {
    title: "Blog | Wavai",
    description: "Read the latest from Wavai.",
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-32 pb-20 text-center">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            {t("Post Not Found", "المقال غير موجود")}
          </h1>
          <p className="text-muted-foreground mb-6">
            {t("The article you're looking for doesn't exist.", "المقال الذي تبحث عنه غير موجود.")}
          </p>
          <Button variant="hero" asChild><Link to={localePath("/blog")}>{t("Back to Blog", "العودة للمدوّنة")}</Link></Button>
        </main>
        <Footer />
      </div>
    );
  }

  const displayTitle = lang === "ar" && post.arabic_title ? post.arabic_title : post.title;
  const displayContent = lang === "ar" && post.arabic_content ? post.arabic_content : post.content;
  const displayExcerpt = lang === "ar" && post.arabic_excerpt ? post.arabic_excerpt : post.excerpt;
  const categoryName = post.blog_categories?.name || "Uncategorized";

  const formattedDate = new Date(post.published_at).toLocaleDateString(
    lang === "ar" ? "ar-SA" : "en-US",
    { year: "numeric", month: "long", day: "numeric" }
  );
  const wordCount = displayContent.split(/\s+/).length;
  const readingTime = Math.max(1, Math.ceil(wordCount / 200));

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shareText = displayTitle;
  const shareLinks = {
    x: `https://x.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + " " + shareUrl)}`,
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 lg:pt-24">
        <div className="container px-6 pt-6">
          <button onClick={() => navigate(localePath("/blog"))} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
            <ArrowLeft className="h-4 w-4" /> {t("Back to Blog", "العودة للمدوّنة")}
          </button>
        </div>

        <article className="container px-6 py-8 lg:py-12 font-sans" dir={lang === "ar" ? "rtl" : "ltr"}>
          <div className="max-w-3xl mx-auto">
            <Badge variant="secondary" className="mb-4">{categoryName}</Badge>
            <h1 className="text-3xl lg:text-4xl font-bold text-foreground leading-tight mb-6">{displayTitle}</h1>
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8">
              <span className="flex items-center gap-1"><User className="h-4 w-4" />{post.author}</span>
              <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />{formattedDate}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{t(`${readingTime} min read`, `${readingTime} دقائق قراءة`)}</span>
            </div>

            {post.cover_image && (
              <div className="rounded-xl overflow-hidden mb-10 border border-border">
                <img src={post.cover_image} alt={displayTitle} className="w-full h-auto" />
              </div>
            )}

            <div className="prose prose-lg max-w-none dark:prose-invert prose-headings:font-sans prose-headings:font-medium prose-headings:text-foreground prose-p:text-muted-foreground prose-a:text-primary prose-strong:text-foreground prose-li:text-muted-foreground">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{displayContent}</ReactMarkdown>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="mt-10 flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <Badge key={tag} variant="outline" className="text-xs">{tag}</Badge>
                ))}
              </div>
            )}

            <div className="mt-8 pt-8 border-t border-border">
              <div className="flex items-center gap-3">
                <Share2 className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  {t("Share this article:", "شارك هذا المقال:")}
                </span>
                <div className="flex gap-2">
                  {([
                    { key: "x", icon: XLogo, url: shareLinks.x },
                    { key: "linkedin", icon: LinkedinLogo, url: shareLinks.linkedin },
                    { key: "facebook", icon: FacebookLogo, url: shareLinks.facebook },
                    { key: "whatsapp", icon: WhatsappLogo, url: shareLinks.whatsapp },
                  ]).map(({ key, icon: Icon, url }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center h-8 w-8 rounded-full border border-border text-muted-foreground hover:text-primary hover:border-primary transition-colors"
                    >
                      <Icon size={16} weight="regular" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div className="mt-12 p-8 rounded-xl bg-gradient-hero border border-border text-center">
              <h3 className="text-xl font-bold text-foreground mb-2">
                {t("Ready to grow your business?", "هل أنت مستعد لتنمية أعمالك؟")}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t(
                  "Let Wavai help you build, launch, and scale your ecommerce presence.",
                  "دع Wavai يساعدك في بناء وإطلاق وتوسيع تواجدك في التجارة الإلكترونية."
                )}
              </p>
              <Button variant="hero" size="lg" asChild>
                <Link to={localePath("/contact")}>{t("Start a Project", "ابدأ مشروعك")}</Link>
              </Button>
            </div>

            <RelatedPosts posts={relatedPosts} />
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
